import { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  VENUE_LOCATION_GROUPS,
  ALL_VENUE_LOCATIONS,
} from "@/lib/venueLocations";

// Controlled vocabulary picker for venue-relative location labels.
// Renders as a native <select> with optgroups for the standard
// vocabulary, plus an "Other (custom)…" option that toggles a
// text input. Empty value = "— none —".
//
// Used by:
//   - AdminVenues house-feed catalog editor (venue_house_feeds.location_label)
//   - AdminLoadIn per-membership service-location column (memberships.
//     service_location_label)

const OTHER_SENTINEL = "__other__";

export function LocationSelect({
  value,
  onChange,
  className = "",
  disabled = false,
  placeholder = "— none —",
}: {
  value: string;
  onChange: (v: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
}) {
  // The "Other" mode is sticky once entered — even if the operator clears
  // the text input back to "", we don't auto-switch back to dropdown mode.
  // They can pick "— none —" or any standard option to reset.
  const [customMode, setCustomMode] = useState<boolean>(
    () => !!value && !ALL_VENUE_LOCATIONS.includes(value),
  );

  if (customMode) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Custom location"
          disabled={disabled}
          className="h-8"
        />
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground px-1"
          onClick={() => {
            setCustomMode(false);
            onChange("");
          }}
          disabled={disabled}
          title="Switch back to the standard list"
        >
          ↺
        </button>
      </div>
    );
  }

  return (
    <select
      value={value || ""}
      disabled={disabled}
      onChange={(e) => {
        const v = e.target.value;
        if (v === OTHER_SENTINEL) {
          setCustomMode(true);
          onChange("");
        } else {
          onChange(v);
        }
      }}
      className={`h-8 px-2 text-sm rounded bg-background border border-input w-full ${className}`}
    >
      <option value="">{placeholder}</option>
      {VENUE_LOCATION_GROUPS.map((g) => (
        <optgroup key={g.label} label={g.label}>
          {g.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </optgroup>
      ))}
      <option value={OTHER_SENTINEL}>Other (type custom)…</option>
    </select>
  );
}
